"use client"

import { useId } from "react"
import Link from "next/link"
import { useField, useFormikContext } from "formik"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"

type FormSelectOption<TValue extends string> = {
  value: TValue
  label: string
  disabled?: boolean
  description?: string
  action?: { label: string; href: string }
}

type FormSelectProps<TValue extends string> = {
  name: string
  label: string
  options: readonly FormSelectOption<TValue>[]
  description?: string
  placeholder?: string
  emptyMessage?: string
  disabled?: boolean
  onValueChange?: (value: TValue) => void
  separateOptions?: boolean
  resetOnChange?: readonly string[]
}

export function FormSelect<TValue extends string>({
  name,
  label,
  options,
  description,
  placeholder,
  emptyMessage,
  disabled = false,
  onValueChange,
  resetOnChange,
  separateOptions = false,
}: FormSelectProps<TValue>) {
  const inputId = useId()
  const [field, meta] = useField<TValue>(name)
  const { setFieldTouched, setFieldValue, setValues } = useFormikContext<Record<string, unknown>>()
  const error = meta.touched && meta.error ? meta.error : null

  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <Label htmlFor={inputId}>{label}</Label>
      </div>
      <Select
        disabled={disabled}
        value={field.value}
        onValueChange={(value) => {
          if (resetOnChange?.length) {
            void setValues(current => {
              const next = { ...current, [name]: value }
              for (const fieldName of resetOnChange) next[fieldName] = ""
              return next
            })
          } else {
            void setFieldValue(name, value)
          }
          void setFieldTouched(name, true, false)
          onValueChange?.(value as TValue)
        }}
      >
        <SelectTrigger id={inputId} aria-invalid={Boolean(error)}>
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent emptyMessage={emptyMessage} className="max-w-[calc(100vw-2rem)]"
          onKeyDown={event => {
            // Radix suppresses Tab in a listbox; make the setup links reachable.
            if (event.key !== "Tab") return
            const links = Array.from(event.currentTarget.querySelectorAll<HTMLAnchorElement>("a[href]"))
            if (!links.length) return
            event.preventDefault()
            const currentIndex = links.findIndex(link => link === document.activeElement)
            const step = event.shiftKey ? -1 : 1
            let nextIndex = (currentIndex + step + links.length) % links.length
            if (currentIndex < 0) nextIndex = event.shiftKey ? links.length - 1 : 0
            links[nextIndex].focus()
          }}>
          {options.map((option) => (
            <div key={option.value} className={separateOptions ? "border-b border-border py-1 last:border-b-0" : undefined}>
            <SelectItem value={option.value} disabled={option.disabled} description={option.description ? <span className="block max-w-lg whitespace-normal break-words">{option.description}</span> : undefined}
              aria-description={option.description}
              className={option.disabled && option.description ? "data-[disabled]:opacity-100 data-[disabled]:text-muted-foreground" : undefined}>
              {option.label}
            </SelectItem>
            {option.disabled && option.action ? (
              <Link href={option.action.href} className="mb-1.5 ml-8 inline-block text-xs text-blue-600 underline underline-offset-2 focus-visible:outline focus-visible:outline-2"
                onPointerDown={event => event.stopPropagation()}
                onClick={event => event.stopPropagation()}>
                <span>{option.action.label}</span>
              </Link>
            ) : null}
            </div>
          ))}
        </SelectContent>
      </Select>
      {description ? <p className="text-xs text-muted-foreground">{description}</p> : null}
      {error ? <p className="text-xs font-medium text-destructive">{error}</p> : null}
    </div>
  )
}
